import type { JobEventSummary, ProjectDetail, RunSummary } from '@shared/api';
import { JOB_PHASE_LABELS, JOB_PHASES, roleAtLeast, type JobProgress, type JobStatus } from '@shared/models';
import { sourceLabel } from '@shared/sources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Bookmark, Check, History, Pause, Play, RefreshCw, RotateCcw } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader, Stat } from '../../components/ui/card';
import { Alert, Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';
import { errorMessage, useMe } from '../../lib/session';

interface JobResponse {
  run: RunSummary;
  progress: JobProgress;
}

const STATUS_TONE: Record<JobStatus, 'success' | 'accent' | 'warning' | 'danger' | 'neutral'> = {
  completed: 'success',
  running: 'accent',
  queued: 'accent',
  paused: 'warning',
  cancelling: 'warning',
  failed: 'danger',
  cancelled: 'neutral',
};

function isTerminal(status: JobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

function n(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function money(value: number): string {
  return value >= 0.01 ? `$${value.toFixed(2)}` : value > 0 ? '<$0.01' : '$0.00';
}

/** A run can be repeated once it has stopped; repeating one still in flight would duplicate it. */
function canRepeat(run: RunSummary): boolean {
  return isTerminal(run.status);
}

/**
 * "repeat of #2", when this run was started from an earlier one. The run it repeats may have
 * dropped off the end of the list, in which case the lineage is simply not claimed.
 */
function rerunLabel(run: RunSummary, all: RunSummary[]): string | null {
  if (!run.rerunOf) return null;
  const source = all.find((other) => other.id === run.rerunOf);
  return source ? `repeat of #${n(source.runNumber)}` : 'a repeat of an earlier run';
}

/** Horizontal bar; `of` of 0 renders an empty track rather than dividing by zero. */
function Bar({ value, of }: { value: number; of: number }) {
  const pct = of > 0 ? Math.min(100, Math.round((value / of) * 100)) : 0;
  return (
    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted" role="presentation">
      <span className="block h-full rounded-full bg-accent transition-all duration-300" style={{ width: `${String(pct)}%` }} />
    </span>
  );
}

/** The run view: live progress while a research job executes, and its outcome once finished. */
export function ProjectPage() {
  const { id = '' } = useParams();
  const me = useMe();
  const client = useQueryClient();

  const project = useQuery({
    queryKey: queryKeys.project(id),
    queryFn: () => api.get<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}`),
    enabled: id.length > 0,
  });

  const latestRun = project.data?.runs[0];
  const jobId = latestRun?.id;

  const job = useQuery({
    queryKey: queryKeys.job(jobId ?? ''),
    queryFn: () => api.get<JobResponse>(`/api/jobs/${encodeURIComponent(jobId ?? '')}`),
    enabled: Boolean(jobId),
    // Poll while the run is active; the snapshot is rebuilt from the database, so it stays
    // correct even if a Worker is evicted mid-run.
    refetchInterval: (query) => (query.state.data && isTerminal(query.state.data.progress.status) ? false : 2000),
  });

  const events = useQuery({
    queryKey: queryKeys.jobEvents(jobId ?? ''),
    queryFn: () => api.get<JobEventSummary[]>(`/api/jobs/${encodeURIComponent(jobId ?? '')}/events?limit=30`),
    enabled: Boolean(jobId),
    refetchInterval: job.data && !isTerminal(job.data.progress.status) ? 5000 : false,
  });

  const control = useMutation({
    mutationFn: (action: 'pause' | 'resume' | 'cancel' | 'retry') =>
      api.post<{ ok: true }>(`/api/jobs/${encodeURIComponent(jobId ?? '')}/${action}`),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.job(jobId ?? '') }),
        client.invalidateQueries({ queryKey: queryKeys.project(id) }),
      ]);
    },
  });

  /**
   * Start a run.
   *
   * With `fromRunId` the run repeats the configuration that run actually used, read from its
   * stored snapshot — so repeating a run is a true repeat rather than "whatever the project says
   * now". The earlier run is left untouched; this is always a new run, which is what makes the
   * two comparable afterwards.
   */
  const startRun = useMutation({
    mutationFn: (fromRunId?: string) =>
      api.post<RunSummary>(`/api/projects/${encodeURIComponent(id)}/runs`, fromRunId ? { fromRunId } : {}),
    // Settled rather than success: a run that cannot start still records the attempt, so the page
    // must refresh to show it instead of leaving the previous run on screen as if nothing happened.
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.project(id) }),
        client.invalidateQueries({ queryKey: queryKeys.projects }),
      ]);
    },
  });

  /**
   * Save this run's configuration as a template.
   *
   * The whole configuration is stored, so running it again somewhere else only means changing
   * the location — the keywords, attributes, custom questions and rules come along.
   */
  const saveTemplate = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/presets', {
        name: project.data?.name ?? 'Saved research',
        config: project.data?.config,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.presets });
    },
  });

  if (project.isPending) {
    return (
      <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Loading research…
      </div>
    );
  }

  if (project.isError || !project.data) {
    return (
      <div className="mx-auto max-w-[1100px] px-5 py-5">
        <Alert tone="error">{errorMessage(project.error) ?? 'This research project could not be loaded.'}</Alert>
        <Link className="mt-3 inline-block text-sm text-accent underline-offset-4 hover:underline" to="/">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const detail = project.data;
  const progress = job.data?.progress;
  const canControl = me.data ? roleAtLeast(me.data.workspace.role, 'member') : false;
  const running = progress ? !isTerminal(progress.status) : false;
  const phaseIndex = progress ? JOB_PHASES.indexOf(progress.phase) : -1;
  const failedTasks = progress?.tasks.failed ?? 0;
  // Earlier runs, so the history card never repeats the run already shown above it.
  const earlierRuns = detail.runs.slice(1);

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold tracking-tight text-foreground">{detail.name}</h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {detail.config.sources.length} source{detail.config.sources.length === 1 ? '' : 's'} ·{' '}
            {detail.config.attributes.selected.length} attributes
            {detail.runs.length > 0 ? ` · run #${String(detail.runs[0]?.runNumber ?? 1)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {progress ? <Badge tone={STATUS_TONE[progress.status]}>{progress.status}</Badge> : null}
          {canControl && progress && running ? (
            <>
              {progress.status === 'paused' ? (
                <Button size="sm" variant="outline" icon={<Play className="h-3.5 w-3.5" />} onClick={() => control.mutate('resume')}>
                  Resume
                </Button>
              ) : (
                <Button size="sm" variant="outline" icon={<Pause className="h-3.5 w-3.5" />} onClick={() => control.mutate('pause')}>
                  Pause
                </Button>
              )}
              <Button size="sm" variant="destructive" icon={<Ban className="h-3.5 w-3.5" />} onClick={() => control.mutate('cancel')}>
                Cancel
              </Button>
            </>
          ) : null}
          {canControl && progress && !running ? (
            <>
              {failedTasks > 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  icon={<RefreshCw className="h-3.5 w-3.5" />}
                  loading={control.isPending}
                  onClick={() => control.mutate('retry')}
                >
                  Retry {n(failedTasks)} failed step{failedTasks === 1 ? '' : 's'}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="accent"
                icon={<RotateCcw className="h-3.5 w-3.5" />}
                loading={startRun.isPending}
                onClick={() => startRun.mutate(jobId)}
              >
                Run this again
              </Button>
            </>
          ) : null}
          {canControl ? (
            <Button
              size="sm"
              variant="ghost"
              icon={saveTemplate.isSuccess ? <Check className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
              loading={saveTemplate.isPending}
              disabled={saveTemplate.isSuccess}
              onClick={() => saveTemplate.mutate()}
            >
              {saveTemplate.isSuccess ? 'Saved as template' : 'Save as template'}
            </Button>
          ) : null}
        </div>
      </header>

      {control.error ?? startRun.error ?? saveTemplate.error ? (
        <Alert className="mb-3" tone="error">
          {errorMessage(control.error ?? startRun.error ?? saveTemplate.error)}
        </Alert>
      ) : null}

      {canControl && progress && !running && (progress.status === 'failed' || failedTasks > 0) ? (
        <Alert className="mb-3" tone={progress.status === 'failed' ? 'error' : 'warning'}>
          <span className="font-medium">
            {progress.status === 'failed'
              ? `Run #${String(progress.runNumber)} failed.`
              : `Run #${String(progress.runNumber)} finished with ${n(failedTasks)} failed step${failedTasks === 1 ? '' : 's'}.`}
          </span>{' '}
          {failedTasks > 0 ? (
            <>
              <strong>Retry {n(failedTasks)} failed step{failedTasks === 1 ? '' : 's'}</strong> keeps everything already collected and
              re-runs only what failed — use it when the cause was temporary, or once you have connected the source it needed.{' '}
            </>
          ) : null}
          <strong>Run this again</strong> starts a fresh run with the exact same setup, leaving this one intact so you can compare them.
        </Alert>
      ) : null}

      {!jobId ? (
        <Card>
          <EmptyState
            title="This research has not run yet"
            description="It was saved as a draft. Start a run to collect data with the configuration you chose."
            action={
              canControl ? (
                <Button
                  variant="accent"
                  icon={<Play className="h-3.5 w-3.5" />}
                  loading={startRun.isPending}
                  onClick={() => startRun.mutate(undefined)}
                >
                  Start the run
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : !progress ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading run progress…
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="Phase"
              description={running ? 'Updating every few seconds while the run is active.' : 'Final state of this run.'}
            />
            <CardBody>
              <ol className="flex flex-wrap gap-1.5">
                {JOB_PHASES.map((phase, index) => (
                  <li
                    key={phase}
                    className={cn(
                      'rounded-[var(--radius-control)] px-2.5 py-1 text-[13px]',
                      index < phaseIndex
                        ? 'bg-muted text-muted-foreground'
                        : index === phaseIndex
                          ? 'bg-accent-subtle font-medium text-accent'
                          : 'text-muted-foreground',
                    )}
                    aria-current={index === phaseIndex ? 'step' : undefined}
                  >
                    {JOB_PHASE_LABELS[phase]}
                  </li>
                ))}
              </ol>
            </CardBody>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Businesses kept" value={n(progress.filtering.finalAccounts)} hint={`${n(progress.merging.accounts)} before filtering`} />
            <Stat
              label="With phone"
              value={n(progress.contacts.accountsWithPhone)}
              hint={`${n(progress.contacts.phonesFound)} phone numbers`}
            />
            <Stat
              label="With email"
              value={n(progress.contacts.accountsWithEmail)}
              hint={`${n(progress.contacts.emailsFound)} email addresses`}
            />
            <Stat label="People found" value={n(progress.enrichment.peopleFound)} />
          </div>

          <Card>
            <CardHeader title="Discovery by source" description="Target is a ceiling, not a guarantee — a source may simply hold fewer matches." />
            <CardBody className="space-y-3">
              {progress.discovery.map((source) => (
                <div key={source.source}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-[13px]">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      {source.label}
                      <Badge tone={source.status === 'failed' ? 'danger' : source.status === 'done' ? 'success' : 'accent'}>
                        {source.status}
                      </Badge>
                    </span>
                    <span className="tabular text-muted-foreground">
                      {n(source.discovered)} / {n(source.target)}
                      {source.pendingTasks > 0 ? ` · ${n(source.pendingTasks)} pending` : ''}
                      {source.failedTasks > 0 ? ` · ${n(source.failedTasks)} failed` : ''}
                    </span>
                  </div>
                  <Bar value={source.discovered} of={source.target} />
                  {source.lastError ? (
                    <p className="mt-1 text-[13px] text-destructive">{source.lastError.message}</p>
                  ) : null}
                </div>
              ))}
            </CardBody>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader title="Merging & filtering" />
              <CardBody>
                <dl className="space-y-1.5 text-[13px]">
                  {[
                    ['Source records collected', n(progress.merging.sourceRecords)],
                    ['Merged into businesses', n(progress.merging.accounts)],
                    ['Auto-merged duplicates', n(progress.merging.autoMerged)],
                    ['Awaiting duplicate review', n(progress.merging.potentialDuplicates)],
                    ['Discarded — no contact details', n(progress.filtering.discardedNoContact)],
                    ['Discarded — other rules', n(progress.filtering.discardedOther)],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-3">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="tabular font-medium text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Enrichment & usage" description="Costs are estimates — check your provider billing for exact figures." />
              <CardBody>
                <dl className="space-y-1.5 text-[13px]">
                  {[
                    ['Websites visited', `${n(progress.enrichment.websitesVisited)} / ${n(progress.enrichment.websitesTotal)}`],
                    ['Websites failed', n(progress.enrichment.websitesFailed)],
                    ['Custom attributes answered', `${n(progress.ai.done)} / ${n(progress.ai.total)}`],
                    ['Places API requests', n(progress.usage.placesRequests)],
                    ['AI calls', n(progress.usage.aiCalls)],
                    ['Browser renders', n(progress.usage.browserRenders)],
                    [
                      'Estimated cost',
                      money(progress.usage.costUsd.places + progress.usage.costUsd.ai + progress.usage.costUsd.browser),
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-3">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="tabular font-medium text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
              </CardBody>
            </Card>
          </div>

          {progress.errors.length > 0 ? (
            <Card>
              <CardHeader title="Problems" description="Grouped by cause. A source that blocks or rate-limits is reported, never bypassed." />
              <CardBody className="space-y-2">
                {progress.errors.map((error) => (
                  <div key={error.code} className="flex items-start gap-2 text-[13px]">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                    <span>
                      <span className="font-medium text-foreground">{error.title}</span>
                      <span className="tabular text-muted-foreground"> × {n(error.count)}</span>
                      {error.sample ? <span className="block text-muted-foreground">{error.sample}</span> : null}
                    </span>
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}

          {(events.data?.length ?? 0) > 0 ? (
            <Card>
              <CardHeader title="Activity" />
              <ul className="divide-y divide-border">
                {events.data?.map((event) => (
                  <li key={event.id} className="flex items-start gap-3 px-4 py-2 text-[13px]">
                    <span className="tabular w-16 shrink-0 text-muted-foreground">
                      {new Date(event.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <Badge tone={event.level === 'error' ? 'danger' : event.level === 'warn' ? 'warning' : 'neutral'}>
                      {event.level}
                    </Badge>
                    <span className="min-w-0 flex-1 text-foreground">{event.message}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {earlierRuns.length > 0 ? (
            <Card>
              <CardHeader
                title={
                  <span className="flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    Earlier runs
                  </span>
                }
                description="Each run keeps its own results and its own setup. Repeating one runs the setup it used, not the current one."
              />
              <ul className="divide-y divide-border">
                {earlierRuns.map((run) => (
                  <li key={run.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="tabular shrink-0 font-medium text-foreground">#{n(run.runNumber)}</span>
                      <Badge tone={STATUS_TONE[run.status]}>{run.status}</Badge>
                      <span className="tabular text-muted-foreground">
                        {new Date(run.startedAt ?? run.createdAt).toLocaleString()}
                      </span>
                      <span className="truncate text-muted-foreground">
                        {run.sources.map((source) => sourceLabel(source)).join(', ')}
                      </span>
                      {run.failedTasks !== null && run.failedTasks > 0 ? (
                        <span className="text-destructive">
                          {n(run.failedTasks)} failed step{run.failedTasks === 1 ? '' : 's'}
                        </span>
                      ) : null}
                      {rerunLabel(run, detail.runs) ? (
                        <span className="text-muted-foreground">{rerunLabel(run, detail.runs)}</span>
                      ) : null}
                    </div>
                    {canControl && !running && canRepeat(run) ? (
                      <Button
                        className="shrink-0"
                        size="sm"
                        variant="outline"
                        icon={<RotateCcw className="h-3.5 w-3.5" />}
                        loading={startRun.isPending}
                        onClick={() => startRun.mutate(run.id)}
                      >
                        Run again
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
